import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { GigForm, type GigFormData } from '../components/gigs/GigForm';
import { useCreateGig } from '../api/mutations';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from '../utils/toast';
import { clearGigPrefill, getGigPrefill } from '../utils/eventSuccess';

import { ProfileGuard } from '../components/auth/ProfileGuard';

export const Route = createFileRoute('/gigs/create')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  validateSearch: (search: Record<string, unknown>): { fromEvent?: string } =>
    search.fromEvent ? { fromEvent: String(search.fromEvent) } : {},
  component: CreateGig,
});

function buildDatetime(date: string, time: string): string {
  if (!date) return '';
  const now = new Date();
  now.setMinutes(now.getMinutes() + 1);
  const defaultTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const t = time || defaultTime;
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = t.split(':').map(Number);
  // Gig times are sent "as picked": no timezone conversion, no zone marker.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;
}

function CreateGig() {
  const navigate = useNavigate();
  const { mutate: createGig, isPending } = useCreateGig();
  const [isUploading, setIsUploading] = useState(false);
  const { fromEvent } = Route.useSearch();
  const prefill = useMemo(() => (fromEvent ? getGigPrefill(fromEvent) : null), [fromEvent]);
  const initialData = useMemo<Partial<GigFormData> | undefined>(
    () =>
      prefill
        ? {
            title: prefill.title,
            description: prefill.description,
            startDate: prefill.startDate,
            startTime: prefill.startTime,
            endDate: prefill.endDate,
            endTime: prefill.endTime,
            timezone: prefill.timezone,
            locationBody: prefill.locationBody,
          }
        : undefined,
    [prefill],
  );

  const handleSubmit = async (data: GigFormData) => {
    setIsUploading(true);

    // 1. Upload cover image to Cloudinary if a new file was selected
    let imageUrl: string | undefined;
    if (data.image) {
      try {
        const results = await uploadFilesToCloudinary([data.image], {
          folder: 'gig_images',
          uploadPreset: 'service_preset',
        });
        imageUrl = results[0].secure_url;
      } catch {
        toast.error('Image upload failed. Please try again.');
        setIsUploading(false);
        return;
      }
    }

    const hasBudget =
      data.budgetType === 'flat_rate'
        ? !!data.budgetAmount?.trim()
        : !!(data.budgetMin?.trim() || data.budgetMax?.trim());

    const payload: Record<string, unknown> = {
      title: data.title,
      description: data.description,
      additional_information: data.additionalInfo,
      timezone: data.timezone,
      location_body: data.locationBody
        ? {
            address: data.locationBody.address,
            city: data.locationBody.city,
            state: data.locationBody.state,
            country: data.locationBody.country,
            latitude: data.locationBody.latitude,
            longitude: data.locationBody.longitude,
          }
        : undefined,
      budget_type: hasBudget ? data.budgetType : null,
      budget_amount:
        hasBudget && data.budgetType === 'flat_rate' ? data.budgetAmount?.trim() || null : null,
      budget_min: hasBudget && data.budgetType === 'range' ? data.budgetMin?.trim() || null : null,
      budget_max: hasBudget && data.budgetType === 'range' ? data.budgetMax?.trim() || null : null,
      ...(data.startDate ? { start_datetime: buildDatetime(data.startDate, data.startTime) } : {}),
      ...(data.endDate ? { end_datetime: buildDatetime(data.endDate, data.endTime) } : {}),
      application_start_datetime: buildDatetime(
        data.applicationStartDate,
        data.applicationStartTime,
      ),
      ...(data.applicationEndDate
        ? {
            application_end_datetime: buildDatetime(
              data.applicationEndDate,
              data.applicationEndTime,
            ),
          }
        : {}),
    };

    // 2. Attach image URL if available
    if (imageUrl) payload.image = imageUrl;

    // 3. Attach tags
    if (data.tagIds && data.tagIds.length > 0) {
      payload.tags = data.tagIds;
    }

    createGig(payload, {
      onSuccess: (responseData) => {
        setIsUploading(false);
        clearGigPrefill();
        const gigId =
          responseData?.data?.gig_uuid ??
          responseData?.gig_uuid ??
          responseData?.id ??
          responseData?.uuid;
        if (gigId) {
          navigate({
            to: '/gigs/$gigId',
            params: { gigId: String(gigId) },
            search: { apply: false },
          });
        } else {
          navigate({ to: '/gigs' });
        }
      },
      onError: () => {
        setIsUploading(false);
        // Handled by mutation
      },
    });
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <ProfileGuard>
        <main className="pt-24 md:pt-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-white/5 rounded-full transition-colors group"
            >
              <FiArrowLeft
                size={24}
                className="text-zinc-400 group-hover:text-white transition-colors"
              />
            </button>
          </div>

          {prefill && (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-4">
              <p className="text-sm text-zinc-400">
                Creating a gig for <span className="text-white">{prefill.eventTitle}</span> — event
                details are pre-filled.
              </p>
            </div>
          )}

          <GigForm
            initialData={initialData}
            title="New Gig"
            submitLabel="Publish"
            onSubmit={handleSubmit}
            isLoading={isPending || isUploading}
          />
        </main>
      </ProfileGuard>

      <BottomNav />
    </div>
  );
}
